import { OperatorFunction, catchError, of, throwError } from 'rxjs';

import { ApiError } from '../models/api-error.models';

/**
 * Turns anything an HTTP call can fail with into an `ApiError`.
 *
 * `apiErrorInterceptor` runs this on every failed request, so subscribers normally receive an
 * `ApiError` already. Handlers still call it on what they catch: it returns an `ApiError`
 * unchanged, it types the value, and it also copes with what reaches a handler without passing
 * the interceptor — a raw `HttpErrorResponse` in a service spec, a `{ status: 404 }` test double,
 * an MSAL rejection, a plain `Error`.
 *
 * It reads the `ErrorResponse` envelope (`{ error, details?, statusCode, timestamp,
 * fieldErrors? }`) every API error answers with. Its `error` is a fixed category label
 * ("Validation error", "Conflict"), not a sentence, so only `details` is read — and, failing that,
 * the first per-field message. Those two keys are read from any object body, without requiring the
 * rest of the envelope; no other key is read, so an anonymous `{ message }` or `{ error }` body,
 * the `ProblemDetails` the framework writes for an authorization filter's bare 401, a
 * plain-text or HTML page from a proxy, and the envelope a blob-typed request (a file export)
 * receives as a `Blob` all yield no server text.
 *
 * Server text becomes `details` only when it is meant for the user: on a 4xx or a 503, and only
 * when it reads like a sentence. A 500 body is never shown — it is not written for users.
 */
export function toApiError(err: unknown): ApiError {
  if (isApiError(err)) {
    return err;
  }

  if (!isHttpShaped(err)) {
    return build(0, undefined, undefined, undefined, UNEXPECTED_MESSAGE);
  }

  const status = err.status;
  // A request made with responseType 'text' receives even a JSON error body as a string; any other
  // string (a proxy's page, plain text) carries nothing to read.
  const body =
    typeof err.error === 'string' ? parseJsonObject(err.error) : err.error;
  const fieldErrors = readFieldErrors(body);
  const details = isMessageBearing(status)
    ? readServerMessage(body, fieldErrors)
    : undefined;
  const url = typeof err.url === 'string' ? err.url : undefined;

  return build(status, details, url, fieldErrors);
}

/**
 * Treats a 404 as success and emits `value` in its place; any other failure is rethrown
 * unchanged. For a delete or clear whose target is already gone: that is the end state the caller
 * wanted (a stale screen, another tab, a retry after a partial failure).
 */
export function recoverNotFound<T, R>(value: R): OperatorFunction<T, T | R> {
  return catchError((error: unknown) =>
    toApiError(error).isNotFound ? of(value) : throwError(() => error),
  );
}

/**
 * The sentence to show for a failed call. The API's own when it sent one. Otherwise, for the
 * failures a caller's "Failed to save X" cannot explain — no response at all, a session or
 * permission refusal (the authorization filters' 401/403 have no sentence), an upstream outage —
 * the generic sentence for the status. Otherwise the caller's `fallback`, which names what failed.
 */
export function describeApiError(err: unknown, fallback: string): string {
  const apiError = toApiError(err);
  if (apiError.details) {
    return apiError.details;
  }
  // Status 0 means "no response" only for an HTTP failure; anything else is the caller's to word.
  const noResponse = apiError.status === 0 && isHttpShaped(err);
  const callerCannotExplain =
    noResponse ||
    apiError.isUnauthenticated ||
    apiError.isForbidden ||
    apiError.isServiceUnavailable;
  return callerCannotExplain ? apiError.message : fallback;
}

/** Whether a value is already an `ApiError`, so `toApiError` can hand it back untouched. */
export function isApiError(value: unknown): value is ApiError {
  const candidate = value as Partial<ApiError> | null | undefined;
  return (
    !!candidate &&
    typeof candidate === 'object' &&
    typeof candidate.status === 'number' &&
    typeof candidate.message === 'string' &&
    typeof candidate.isServerError === 'boolean'
  );
}

const UNEXPECTED_MESSAGE = 'An unexpected error occurred. Please try again.';

/** What `message` says when the server sent nothing the user should read. */
const GENERIC_MESSAGES: Record<number, string> = {
  0: 'Unable to reach the server. Please check your connection and try again.',
  400: 'The request was not valid. Please check your input and try again.',
  401: 'Your session has expired. Please sign in again.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested item was not found.',
  409: 'This change conflicts with the current data. Please refresh and try again.',
  422: 'This change could not be applied.',
  503: 'A service this page depends on is temporarily unavailable. Please try again shortly.',
};

function genericMessage(status: number): string {
  if (GENERIC_MESSAGES[status]) {
    return GENERIC_MESSAGES[status];
  }
  if (status >= 500) {
    return 'Something went wrong on the server. Please try again later.';
  }
  return UNEXPECTED_MESSAGE;
}

function build(
  status: number,
  details: string | undefined,
  url: string | undefined,
  fieldErrors: Record<string, string[]> | undefined,
  fallback = genericMessage(status),
): ApiError {
  return {
    status,
    message: details ?? fallback,
    details,
    url,
    fieldErrors,
    isValidation: status === 400,
    isUnauthenticated: status === 401,
    isForbidden: status === 403,
    isNotFound: status === 404,
    isConflict: status === 409 || status === 422,
    isServiceUnavailable: status === 503,
    isServerError: status >= 500,
  };
}

/**
 * An `HttpErrorResponse`, or anything shaped like one. Structural rather than `instanceof` so
 * component specs can keep throwing `{ status: 404 }` doubles.
 */
function isHttpShaped(
  value: unknown,
): value is { status: number; error?: unknown; url?: unknown } {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as { status?: unknown }).status === 'number'
  );
}

/** 4xx bodies are written for the caller; of the 5xx, only the 503's static copy is. */
function isMessageBearing(status: number): boolean {
  return (status >= 400 && status < 500) || status === 503;
}

function readServerMessage(
  body: unknown,
  fieldErrors: Record<string, string[]> | undefined,
): string | undefined {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const fields = body as Record<string, unknown>;
  const firstFieldError = fieldErrors
    ? Object.values(fieldErrors)[0]?.[0]
    : undefined;
  for (const candidate of [fields['details'], firstFieldError]) {
    const text = asSafeText(candidate);
    if (text) {
      return text;
    }
  }
  return undefined;
}

/**
 * The per-field messages of a validation 400, keeping only entries that really are lists of
 * strings: this runs inside error handlers, where a throw on a malformed body would cost the
 * user the error entirely.
 */
function readFieldErrors(body: unknown): Record<string, string[]> | undefined {
  const raw = (body as { fieldErrors?: unknown } | null | undefined)
    ?.fieldErrors;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined;
  }

  const result: Record<string, string[]> = {};
  for (const [field, messages] of Object.entries(raw)) {
    if (!Array.isArray(messages)) continue;
    const texts = messages.filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0,
    );
    if (texts.length > 0) {
      result[field] = texts;
    }
  }
  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * The value when it reads like a sentence for the user. Rejected rather than truncated: text cut
 * mid-word reads worse than generic copy. The length cap clears the longest legitimate message
 * (the practice-area rename conflict, ~330 characters with a 200-character name). Markup is not
 * refused: `details` can quote a user's own input back (a practice-area name containing "<").
 */
function asSafeText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const readsLikeProse = value.trim().length > 0 && value.length <= 500;
  return readsLikeProse ? value : undefined;
}

/** The object a string body holds when it is JSON, otherwise undefined. */
function parseJsonObject(text: string): Record<string, unknown> | undefined {
  if (!text.trimStart().startsWith('{')) return undefined;
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * CSS class for a signed numeric value shown in the time-off balance UI:
 * green while positive, red once negative, neutral at zero. Shared between the
 * balance table and the balance-info dialog so their coloring can't drift.
 */
export function balanceValueClass(value: number): string {
  if (value > 0) return 'balance-value--positive';
  if (value < 0) return 'balance-value--negative';
  return '';
}
